// src/components/MonthlyBooksChart.js
import React, { useState, useEffect } from 'react';
import { WEB_APP_URL } from '../config';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Small component to handle cover image with fallback
const CoverImage = ({ src, alt, isFiveStar, title }) => {
  const [failed, setFailed] = useState(false);
  
  if (!src || failed) {
    return (
      <div className="tiny-cover-placeholder" title={alt}>
        📖
      </div>
    );
  }
  
  return (
    <img
      src={src}
      alt={alt}
      className={`tiny-cover ${isFiveStar ? 'five-star' : ''}`}
      title={title}
      onError={() => setFailed(true)}
    />
  );
};

const getCache = (key) => {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
};

const MonthlyBooksChart = ({ refreshToggle, selectedYear }) => {
  const [books, setBooks] = useState(() => getCache('books') || []);
  const [covers, setCovers] = useState(() => getCache('covers') || {});
  const [loading, setLoading] = useState(!getCache('books'));
  const targetYear = selectedYear || new Date().getFullYear();

  useEffect(() => {
    const fetchBooks = async () => {
      if (!getCache('books')) setLoading(true);
      try {
        const response = await fetch(`${WEB_APP_URL}?action=getBooks`);
        const result = await response.json();
        setBooks(result.books || []);
        setCovers(getCache('covers') || {});
      } catch (error) {
        console.error('Error fetching books for chart:', error);
      }
      setLoading(false);
    };
    fetchBooks();
  }, [refreshToggle]);

  // Sync covers from cache periodically (BooksTable updates it async)
  useEffect(() => {
    const interval = setInterval(() => {
      const cached = getCache('covers') || {};
      setCovers(prev => {
        if (Object.keys(cached).length !== Object.keys(prev).length) {
          return cached;
        }
        return prev;
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Group books by month
  const booksByMonth = {};
  MONTHS.forEach((_, i) => booksByMonth[i] = []);
  
  books.filter(book => {
    if (book.date) {
      return new Date(book.date).getFullYear() === targetYear;
    }
    return false;
  }).forEach(book => {
    const month = new Date(book.date).getMonth();
    booksByMonth[month].push(book);
  });

  const maxBooks = Math.max(...Object.values(booksByMonth).map(arr => arr.length), 1);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;
  }

  const handleExpand = () => {
    window.open(`/viz.html?year=${targetYear}`, '_blank');
  };

  return (
    <div className="monthly-covers-chart">
      <div className="chart-header-row">
        <span></span>
        <button className="expand-btn" onClick={handleExpand} title="Expand to full view">⛶</button>
      </div>
      <div className="covers-chart-container">
        {MONTHS.map((month, i) => {
          const monthBooks = booksByMonth[i];
          return (
            <div key={month} className="covers-month-column">
              <div className="covers-stack" style={{ minHeight: `${maxBooks * 28}px` }}>
                {monthBooks.map((book, idx) => {
                  const coverUrl = covers[book.title] || book.coverUrl;
                  const isFiveStar = parseInt(book.rating, 10) === 5;
                  const cleanTitle = book.title.replace(/\s+by\s+.+$/i, '').trim();
                  return (
                    <CoverImage
                      key={idx}
                      src={coverUrl}
                      alt={cleanTitle}
                      isFiveStar={isFiveStar}
                      title={`${cleanTitle} ${'★'.repeat(book.rating || 0)}`}
                    />
                  );
                })}
              </div>
              <div className="covers-month-count">{monthBooks.length || ''}</div>
              <div className="covers-month-label">{month}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthlyBooksChart;
